import { useRef } from 'react';
import { HttpTransportType, HubConnectionBuilder } from '@microsoft/signalr';
import { useConfig } from 'hooks/use-config';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { clients } from 'lib/https';
import { useNotificationStore } from '../state/store/common/useNotificationStore';

interface NotificationChannelConfig {
  name?: string;
  notifyMethod: string;
}

const DEFAULT_CHANNELS: NotificationChannelConfig[] = [
  { name: 'Notification', notifyMethod: 'ReceiveNotification' },
];

export function useNotification() {
  const { configurationSettings } = useConfig();
  const { handleNotification } = useNotificationStore();
  const disconnect = useRef<(() => Promise<void>) | null>(null);

  const logicBaseUrl = configurationSettings.paths.logicBaseUrl;
  const projectKey = configurationSettings.blocksKey;

  const fetchChannels = async (): Promise<NotificationChannelConfig[]> => {
    try {
      const url = `${logicBaseUrl}/Notification/Gets?page=0&pageSize=100&projectKey=${projectKey}`;
      const data = await clients.get<{ configurations?: NotificationChannelConfig[] }>(url, projectKey);
      if (data?.configurations && data.configurations.length > 0) {
        return data.configurations;
      }
      return DEFAULT_CHANNELS;
    } catch (error) {
      console.warn('Notification config fetch error, using default channels:', error);
      return DEFAULT_CHANNELS;
    }
  };

  const connect = async () => {
    if (!logicBaseUrl || !projectKey) {
      console.warn('Cannot connect to notification service: logic base url / project key missing');
      return;
    }

    const channels = await fetchChannels();

    const connection = new HubConnectionBuilder()
      .withUrl(`${logicBaseUrl}/NotificationHub?x-blocks-key=${projectKey}`, {
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () => useAuthStore.getState().accessToken || '',
      })
      .withAutomaticReconnect()
      .build();

    channels.forEach(({ name, notifyMethod }) => {
      connection.on(notifyMethod, (message: unknown) =>
        handleNotification(name ?? notifyMethod, message),
      );
    });

    disconnect.current = async () => {
      if (connection.state !== 'Disconnected') {
        await connection.stop();
      }
    };

    try {
      await connection.start();
    } catch (error) {
      console.error('Notification SignalR connection failed', error);
    }
  };

  return { connect, disconnect };
}
